import type {ReactNode} from 'react'
import {clsx} from 'clsx'
import {useCollapseAnimation, COLLAPSE_ANIMATION_DURATION_MS} from '../../hooks/use-collapse-animation'
import styles from './CollapsibleRegion.module.css'

interface CollapsibleRegionProps {
  /** Whether the region is open (expanded). */
  open: boolean
  /**
   * Keep the children mounted while collapsed instead of unmounting after the
   * collapse animation. Use for regions whose children must stay alive while
   * hidden (e.g. a session that keeps polling its logs).
   */
  keepMounted?: boolean
  /** Optional class applied to the animating grid wrapper. */
  className?: string
  children: ReactNode
}

/**
 * Wraps content in a fixed-duration grid-rows collapse/expand animation.
 *
 * The outer grid transitions `grid-template-rows` between `0fr` and `1fr`; the
 * inner element clips the overflow so the content height animates smoothly.
 * While collapsed the region is marked `inert` because a `0fr` grid row still
 * leaves its content focusable and in the accessibility tree (unlike
 * `display: none`).
 */
export function CollapsibleRegion({open, keepMounted = false, className, children}: CollapsibleRegionProps) {
  const {shouldRender, isExpanded} = useCollapseAnimation(open, {keepMounted})

  if (!shouldRender) return null

  return (
    <div
      className={clsx(styles.grid, isExpanded && styles.expanded, className)}
      // Derive the CSS transition duration from the JS constant so the grid
      // animation and the unmount timer in useCollapseAnimation stay in lockstep.
      style={{'--agent-collapse-duration': `${COLLAPSE_ANIMATION_DURATION_MS}ms`}}
    >
      <div className={styles.inner} {...(isExpanded ? {} : {inert: true})}>
        {children}
      </div>
    </div>
  )
}
